/**
 * LevelStepper — pick one of N ordered levels on a horizontal track.
 *
 * Radio semantics (react-aria RadioGroup): arrow keys move the selection,
 * every step is a labelled, pressable node, and assistive tech reads it as
 * "Most updates, 3 of 4". Visually the nodes sit on a track that fills up to
 * the selected step, so the ordering reads at a glance — which a bare range
 * input with tick marks never does.
 *
 * Use it for a small set (3–6) of NAMED, ORDERED options where each option is
 * a superset of the last (autonomy levels, plan tiers, verbosity). For
 * unordered options use RadioGroup / RadioCard; for a continuous value use
 * Slider.
 */

import { useCallback, useMemo } from 'react';
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
} from 'react-aria-components';

import { cx } from '@/utils/cx';

export interface LevelStep {
  value: string;
  label: string;
}

export interface LevelStepperProps {
  steps: readonly LevelStep[];
  value: string;
  /**
   * A step being previewed (typically the hovered one, echoed back by the
   * consumer after `onPreview`). The track shows a ghost fill to it so the
   * stepper agrees with whatever else the consumer previews.
   */
  previewValue?: string | null;
  onChange(value: string): void;
  /**
   * Fires with a step's value while the pointer hovers it, and with `null`
   * when it leaves — so a consumer can preview the consequence of a step
   * before it is chosen. Never fires while disabled.
   */
  onPreview?(value: string | null): void;
  isDisabled?: boolean;
  'aria-label': string;
  className?: string;
}

interface StepperTrackProps {
  stepCount: number;
  selectedIndex: number;
  previewIndex: number | null;
}

function fillPercent(stepCount: number, index: number): number {
  return stepCount > 1 ? (index / (stepCount - 1)) * 100 : 0;
}

/**
 * The track runs from the centre of the first node to the centre of the last.
 * Each node owns an equal column, so the centres sit at 50/n% and 100−50/n%;
 * those insets are the only dynamic styles and are isolated here. A preview
 * draws a translucent fill to the previewed step beneath the real one.
 */
function StepperTrack({
  stepCount,
  selectedIndex,
  previewIndex,
}: StepperTrackProps) {
  const style = useMemo(() => {
    const half = stepCount > 0 ? 50 / stepCount : 0;
    return { left: `${half}%`, right: `${half}%` };
  }, [stepCount]);
  const fillStyle = useMemo(
    () => ({ width: `${fillPercent(stepCount, selectedIndex)}%` }),
    [stepCount, selectedIndex]
  );
  const previewStyle = useMemo(
    () => ({
      width: `${fillPercent(stepCount, previewIndex ?? selectedIndex)}%`,
    }),
    [stepCount, previewIndex, selectedIndex]
  );
  return (
    <div
      aria-hidden='true'
      // Vertically centred on the node: (node 20px − track 2px) / 2. Keep in
      // step with StepNode's `size-5`.
      className='pointer-events-none absolute top-[9px] h-0.5 rounded-full bg-quaternary'
      style={style}
    >
      <div
        className='absolute inset-y-0 left-0 rounded-full bg-brand-solid opacity-30 transition-[width] duration-200 ease-out motion-reduce:transition-none'
        style={previewStyle}
      />
      <div
        className='absolute inset-y-0 left-0 rounded-full bg-brand-solid transition-[width] duration-200 ease-out motion-reduce:transition-none'
        style={fillStyle}
      />
    </div>
  );
}

interface StepNodeProps {
  step: LevelStep;
  index: number;
  selectedIndex: number;
  onPreview?(value: string | null): void;
}

function StepNode({ step, index, selectedIndex, onPreview }: StepNodeProps) {
  const isPassed = index < selectedIndex;
  const handleHoverStart = useCallback(() => {
    onPreview?.(step.value);
  }, [onPreview, step.value]);
  const handleHoverEnd = useCallback(() => {
    onPreview?.(null);
  }, [onPreview]);
  return (
    <AriaRadio
      value={step.value}
      onHoverStart={handleHoverStart}
      onHoverEnd={handleHoverEnd}
      className={({ isDisabled }) =>
        cx(
          'group relative flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-1.5 outline-hidden',
          isDisabled && 'cursor-not-allowed'
        )
      }
    >
      {({ isSelected, isFocusVisible }) => (
        <>
          <span
            className={cx(
              'relative z-10 flex size-5 items-center justify-center rounded-full transition-[background-color,box-shadow] duration-200 ease-out motion-reduce:transition-none',
              isSelected || isPassed
                ? 'bg-brand-solid'
                : 'bg-primary ring-1 ring-primary ring-inset group-hover:ring-brand',
              isSelected &&
                'shadow-[0_0_0_4px_var(--color-bg-brand-secondary)]',
              isFocusVisible && 'outline-2 outline-offset-4 outline-focus-ring'
            )}
          >
            {/* Included steps are solid; only the selected one carries the
                dot. No checkmarks — those read as "steps completed", and
                these are cumulative levels, not a wizard. The dot stays
                mounted and cross-fades so a change animates rather than
                cuts. */}
            <span
              className={cx(
                'absolute size-2 rounded-full bg-fg-white transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                isSelected ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
              )}
            />
          </span>
          {/* Constant weight: a bold-on-select label reflows its centred
              neighbours sideways on every change. Colour carries selection. */}
          <span
            className={cx(
              // text-xs below sm: four equal columns at phone width are ~70px
              // each, and a 14px "Everything" overflows its column.
              'max-w-full px-1 text-center text-xs font-medium leading-tight transition-colors duration-200 ease-out motion-reduce:transition-none sm:text-sm',
              isSelected
                ? 'text-fg-primary'
                : 'text-fg-tertiary group-hover:text-fg-secondary'
            )}
          >
            {step.label}
          </span>
        </>
      )}
    </AriaRadio>
  );
}

export function LevelStepper({
  steps,
  value,
  previewValue = null,
  onChange,
  onPreview,
  isDisabled,
  className,
  'aria-label': ariaLabel,
}: LevelStepperProps) {
  const selectedIndex = Math.max(
    0,
    steps.findIndex(step => step.value === value)
  );
  const previewIndexRaw =
    previewValue === null
      ? -1
      : steps.findIndex(step => step.value === previewValue);
  const previewIndex = previewIndexRaw < 0 ? null : previewIndexRaw;
  return (
    <AriaRadioGroup
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      orientation='horizontal'
      aria-label={ariaLabel}
      className={cx(
        'relative flex w-full items-start',
        isDisabled && 'opacity-50',
        className
      )}
    >
      <StepperTrack
        stepCount={steps.length}
        selectedIndex={selectedIndex}
        previewIndex={previewIndex}
      />
      {steps.map((step, index) => (
        <StepNode
          key={step.value}
          step={step}
          index={index}
          selectedIndex={selectedIndex}
          onPreview={isDisabled ? undefined : onPreview}
        />
      ))}
    </AriaRadioGroup>
  );
}
